// frontend/src/marketing/Markdown.jsx
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PropTypes from "prop-types";

/** Stable slug from heading text — must match the TOC builder in docsContent.js. */
export function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/** Flatten React children to a plain string (for heading ids). */
function toText(children) {
  return React.Children.toArray(children)
    .map((c) => (typeof c === "string" ? c : c?.props?.children ? toText(c.props.children) : ""))
    .join("");
}

// react-markdown v9 no longer passes an `inline` flag, and emits <pre> ONLY for
// fenced/block code (never inline). So we detect block code at the <pre> level
// (reliable for fences with or without a language) and keep <code> for inline
// styling. A `code` whose class carries a language- token is part of a block, so
// it skips the inline pill.
function CodePre({ children }) {
  const [copied, setCopied] = useState(false);
  const text = toText(children);
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => {
          if (navigator?.clipboard) navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="absolute right-2 top-2 rounded-md bg-white/10 px-2 py-1 text-xs font-medium text-gray-300 opacity-0 transition hover:bg-white/20 group-hover:opacity-100"
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="overflow-x-auto rounded-xl bg-gray-900 p-4 text-sm leading-6 text-gray-100">
        {children}
      </pre>
    </div>
  );
}
CodePre.propTypes = { children: PropTypes.node };
CodePre.defaultProps = { children: null };

function Code({ className, children }) {
  // Block code (inside a <pre>) carries a language- class; render it plain so the
  // <pre> wrapper styles it. Everything else is an inline pill.
  if (className && /language-/.test(className)) {
    return <code className={className}>{children}</code>;
  }
  return (
    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-brand-700">
      {children}
    </code>
  );
}
Code.propTypes = { className: PropTypes.string, children: PropTypes.node };
Code.defaultProps = { className: "", children: null };

const COMPONENTS = {
  h1: ({ children }) => (
    <h1 id={slugify(toText(children))} className="mt-2 scroll-mt-24 text-3xl font-bold tracking-tight text-gray-900">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 id={slugify(toText(children))} className="mt-10 scroll-mt-24 border-b border-gray-100 pb-2 text-2xl font-bold tracking-tight text-gray-900">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 id={slugify(toText(children))} className="mt-8 scroll-mt-24 text-xl font-semibold text-gray-900">
      {children}
    </h3>
  ),
  p: ({ children }) => <p className="mt-4 leading-7 text-gray-700">{children}</p>,
  ul: ({ children }) => <ul className="mt-4 list-disc space-y-2 pl-6 text-gray-700">{children}</ul>,
  ol: ({ children }) => <ol className="mt-4 list-decimal space-y-2 pl-6 text-gray-700">{children}</ol>,
  li: ({ children }) => <li className="leading-7">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-800">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mt-4 border-l-4 border-brand-200 bg-brand-50/50 px-4 py-2 italic text-gray-700">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="border-b border-gray-300">{children}</thead>,
  th: ({ children }) => <th className="px-3 py-2 font-semibold text-gray-900">{children}</th>,
  td: ({ children }) => <td className="border-b border-gray-100 px-3 py-2 text-gray-700">{children}</td>,
  hr: () => <hr className="my-10 border-gray-200" />,
  pre: CodePre,
  code: Code,
};

/** Markdown — renders GitHub-flavoured markdown with the QRFlow doc theme. */
export default function Markdown({ children }) {
  return (
    <div className="max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
Markdown.propTypes = { children: PropTypes.string.isRequired };
